import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Settings as SettingsIcon, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function Settings() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'expense',
    color: '#3b82f6',
    icon: 'circle',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API}/categories`, formData);
      toast.success('Catégorie ajoutée avec succès!');
      setDialogOpen(false);
      setFormData({
        name: '',
        type: 'expense',
        color: '#3b82f6',
        icon: 'circle',
      });
      fetchCategories();
    } catch (error) {
      toast.error("Erreur lors de l'ajout de la catégorie");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie?')) return;

    try {
      await axios.delete(`${API}/categories/${id}`);
      toast.success('Catégorie supprimée avec succès');
      fetchCategories();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const revenueCategories = categories.filter((cat) => cat.type === 'revenue');
  const expenseCategories = categories.filter((cat) => cat.type === 'expense');

  const colorOptions = [
    { value: '#ef4444', label: 'Rouge' },
    { value: '#f97316', label: 'Orange' },
    { value: '#f59e0b', label: 'Ambre' },
    { value: '#eab308', label: 'Jaune' },
    { value: '#84cc16', label: 'Citron' },
    { value: '#10b981', label: 'Vert' },
    { value: '#14b8a6', label: 'Turquoise' },
    { value: '#0ea5e9', label: 'Cyan' },
    { value: '#3b82f6', label: 'Bleu' },
    { value: '#6366f1', label: 'Indigo' },
    { value: '#8b5cf6', label: 'Violet' },
    { value: '#a855f7', label: 'Pourpre' },
    { value: '#ec4899', label: 'Rose' },
    { value: '#f43f5e', label: 'Rose Rouge' },
  ];

  return (
    <div className="space-y-6 fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Paramètres</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Gérez les catégories de revenus et dépenses</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700" data-testid="add-category-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Catégorie
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="category-dialog">
            <DialogHeader>
              <DialogTitle>Nouvelle Catégorie</DialogTitle>
              <DialogDescription>Ajoutez une nouvelle catégorie de revenu ou dépense</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nom de la catégorie</Label>
                <Input
                  id="name"
                  data-testid="category-name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1"
                  placeholder="Ex: Achats Médicaments"
                />
              </div>
              <div>
                <Label htmlFor="type">Type</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })} required>
                  <SelectTrigger className="mt-1" data-testid="category-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Revenu</SelectItem>
                    <SelectItem value="expense">Dépense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="color">Couleur</Label>
                <Select value={formData.color} onValueChange={(value) => setFormData({ ...formData, color: value })}>
                  <SelectTrigger className="mt-1" data-testid="category-color-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {colorOptions.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center">
                          <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: color.value }}></div>
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" data-testid="category-submit-btn" className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700">
                <Tag className="w-4 h-4 mr-2" />
                Ajouter la Catégorie
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Revenue Categories */}
      <Card data-testid="revenue-categories">
        <CardHeader>
          <CardTitle className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
            Catégories de Revenus
          </CardTitle>
          <CardDescription>{revenueCategories.length} catégories</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
            </div>
          ) : revenueCategories.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune catégorie de revenu</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {revenueCategories.map((category) => (
                <div key={category.id} className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md transition-shadow" data-testid="category-item">
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: category.color }}></div>
                    <span className="font-medium text-gray-700">{category.name}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(category.id)} data-testid="delete-category-btn" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expense Categories */}
      <Card data-testid="expense-categories">
        <CardHeader>
          <CardTitle className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
            Catégories de Dépenses
          </CardTitle>
          <CardDescription>{expenseCategories.length} catégories</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
            </div>
          ) : expenseCategories.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune catégorie de dépense</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {expenseCategories.map((category) => (
                <div key={category.id} className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md transition-shadow" data-testid="category-item">
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: category.color }}></div>
                    <span className="font-medium text-gray-700">{category.name}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(category.id)} data-testid="delete-category-btn" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <SettingsIcon className="w-6 h-6 text-cyan-600 mt-1" />
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Gestion des Catégories</h4>
              <p className="text-sm text-gray-600">
                Les catégories vous permettent d'organiser vos revenus et dépenses. Vous pouvez personnaliser le nom et la couleur de chaque catégorie pour une meilleure visualisation dans les graphiques et rapports.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                <strong>Note:</strong> La suppression d'une catégorie n'affecte pas les transactions existantes, mais elles seront affichées comme "Non catégorisé".
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default Settings;
